/**
 * Local draft persistence for in-progress prompt authoring.
 *
 * Protects unsaved work against accidental navigation, crashes, and — the
 * original motivation — a mid-session logout (expired token) that would
 * otherwise discard everything the user had typed/generated.
 *
 * Drafts are stored in localStorage under a caller-provided key as
 * `{ savedAt, data }`. Writes are debounced; empty drafts are never written.
 */
import { useEffect, useRef } from 'react';

export interface StoredDraft<T> {
  savedAt: number;
  data: T;
}

const KEY_PREFIX = 'promptfab_draft:';

function fullKey(key: string): string {
  return key.startsWith(KEY_PREFIX) ? key : `${KEY_PREFIX}${key}`;
}

export function loadDraft<T>(key: string): StoredDraft<T> | null {
  try {
    const raw = localStorage.getItem(fullKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft<T>;
    if (!parsed || typeof parsed.savedAt !== 'number' || parsed.data === undefined) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearDraft(key: string): void {
  try {
    localStorage.removeItem(fullKey(key));
  } catch {
    /* ignore */
  }
}

/**
 * Debounced autosave of `value` to localStorage under `key`.
 * The write only happens while `enabled` is true, so callers can gate on
 * "there is meaningful content to protect".
 */
export function useDraftAutosave<T>(
  key: string,
  value: T,
  enabled = true,
  delayMs = 700
): void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const serialized = JSON.stringify(value);

  useEffect(() => {
    if (!enabled) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        localStorage.setItem(
          fullKey(key),
          JSON.stringify({ savedAt: Date.now(), data: JSON.parse(serialized) })
        );
      } catch {
        /* ignore quota / serialization errors */
      }
    }, delayMs);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [key, serialized, enabled, delayMs]);
}

/** Format a saved-at timestamp as a short relative label (e.g. "2 min ago"). */
export function formatDraftAge(savedAt: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - savedAt) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
